'use client';

/**
 * Interactive Slider Component
 *
 * Renders a carousel/slider with navigation dots and optional auto-play.
 */

import classNames from 'classnames';
import React, { useState, useEffect } from 'react';

import type { SliderConfig } from '../types';

interface InteractiveSliderProps {
  config: SliderConfig;
  className?: string;
}

export const InteractiveSlider: React.FC<InteractiveSliderProps> = ({ config, className }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  // Auto-play effect
  useEffect(() => {
    if (!config.autoPlay || config.items.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => {
        if (config.loop) {
          return (prev + 1) % config.items.length;
        } else {
          return prev < config.items.length - 1 ? prev + 1 : prev;
        }
      });
    }, config.interval);

    return () => clearInterval(interval);
  }, [config.autoPlay, config.interval, config.items.length, config.loop]);

  const goToSlide = (index: number) => {
    setCurrentIndex(index);
  };

  const goToPrevious = () => {
    setCurrentIndex((prev) => {
      if (config.loop) {
        return prev === 0 ? config.items.length - 1 : prev - 1;
      } else {
        return Math.max(0, prev - 1);
      }
    });
  };

  const goToNext = () => {
    setCurrentIndex((prev) => {
      if (config.loop) {
        return (prev + 1) % config.items.length;
      } else {
        return Math.min(config.items.length - 1, prev + 1);
      }
    });
  };

  if (config.items.length === 0) {
    return <div className="slider-empty">No slides available</div>;
  }

  return (
    <div className={classNames('interactive-slider', className)}>
      {/* Slides with absolutely positioned arrow buttons */}
      <div className="slider-content relative">
        {config.items.map((item, index) => (
          <div
            key={index}
            className={classNames('slider-item', {
              hidden: index !== currentIndex,
              block: index === currentIndex,
            })}
            dangerouslySetInnerHTML={{ __html: item.html }}
          />
        ))}

        {/* Side Navigation Arrows - Absolutely positioned */}
        {config.items.length > 1 && (
          <>
            {/* Previous Button - Left side */}
            <button
              onClick={goToPrevious}
              disabled={!config.loop && currentIndex === 0}
              className="absolute left-0 top-1/2 -translate-y-1/2 flex items-center justify-center w-12 h-12 rounded-full bg-[#003049] text-white hover:bg-[#004060] transition-all disabled:opacity-30 disabled:cursor-not-allowed shadow-lg"
              aria-label="Previous slide"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>

            {/* Next Button - Right side */}
            <button
              onClick={goToNext}
              disabled={!config.loop && currentIndex === config.items.length - 1}
              className="absolute right-0 top-1/2 -translate-y-1/2 flex items-center justify-center w-12 h-12 rounded-full bg-[#003049] text-white hover:bg-[#004060] transition-all disabled:opacity-30 disabled:cursor-not-allowed shadow-lg"
              aria-label="Next slide"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </>
        )}
      </div>

      {/* Bottom Navigation Dots - Black */}
      {config.showDots && config.items.length > 1 && (
        <div className="mt-6 flex flex-row items-center justify-center gap-2">
          {config.items.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={classNames(
                'h-2.5 w-2.5 rounded-full transition-all duration-200',
                index === currentIndex
                  ? 'scale-125 bg-[#222]'
                  : 'bg-[#e0e0e0] hover:bg-[#bdbdbd]',
              )}
              aria-label={`Go to slide ${index + 1}`}
              style={{ outline: 'none', border: 'none' }}
            />
          ))}
        </div>
      )}
    </div>
  );
};
